import { SaveOutlined } from "@ant-design/icons";
import { Gear } from "@prisma/client";
import { Alert, Form, Input, Select, Space } from "antd";
import { AxiosInstance } from "axios";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useStateIfMounted } from "use-state-if-mounted";
import ActionBtn from "../../shared/action-btn";
import cleanUrl from "../../shared/clean-url";
import DeleteBtn from "../../shared/delete-btn";
import GearUrl from "../../shared/gearl-url";
import getErrorMessage from "../../shared/get-error-msg";
import get_file_url from "../../shared/get-file-url";
import RichEditor from "../../shared/rich-editor";
import { AppContext } from "../contexts/app-context";
import HttpClient from "../http-client";
import TagList, { TagWithMeta } from "./tag-list";

interface GearFormProps {
  initialValue?: Partial<Gear>;
  onUpdate: (value: Gear) => void;
  onDelete?: (value: Gear) => void;
  httpClient: AxiosInstance | any;
  disableDelete?: boolean;
}
enum Mode {
  create,
  edit,
}
const GearForm: FunctionComponent<GearFormProps> = ({
  initialValue,
  onUpdate,
  onDelete,
  httpClient,
  disableDelete,
}) => {
  const [busy, setBusy] = useStateIfMounted(false);
  const { user } = useContext(AppContext);
  const [serverMsg, setServerMsg] = useStateIfMounted<string | undefined>(
    undefined
  );
  const [serverMsgType, setServerMsgType] = useStateIfMounted<
    "error" | "success" | undefined
  >(undefined);
  const [form] = Form.useForm();
  const mode: Mode = useMemo(() => {
    return initialValue?.id ? Mode.edit : Mode.create;
  }, [initialValue]);
  const self = useMemo(
    () => initialValue?.created_by_id === user?.id,
    [initialValue, user]
  );
  const save = useCallback(
    async (values: Partial<Gear>) => {
      const { id, ...sanitizedValues } = values;
      const tags: TagWithMeta[] = (sanitizedValues as any).tags || [];
      (sanitizedValues as any).tags = tags
        .filter((e) => {
          if (!e.deleted) {
            return true;
          }
          if (mode === Mode.edit && e.deleted) {
            return true;
          }
          return false;
        })
        .map((e) => (e.deleted ? "-" + e.id : e.id));
      setBusy(true);
      setServerMsg(undefined);
      try {
        const { data } =
          mode === Mode.create
            ? await httpClient.post("gear", sanitizedValues)
            : await httpClient.patch(
                `gear/${initialValue?.id}`,
                sanitizedValues
              );

        if (data?.data) {
          setServerMsgType("success");
          setServerMsg("Gear saved!");
          onUpdate(data.data);
        }
      } catch (error: any) {
        if (error?.response?.data?.error) {
          setServerMsgType("error");
          setServerMsg(getErrorMessage(error?.response?.data));
        }
      }
      setBusy(false);
    },
    [mode, onUpdate, initialValue]
  );
  useEffect(() => {
    form.setFieldsValue(initialValue);
  }, [initialValue?.updated_at]);
  const searchGears = useCallback(async (term: string) => {
    try {
      const { data } = await HttpClient.get(`gear?name=${term}&page=-1`);
      const options: Gear[] = Array.isArray(data?.data) ? data?.data : [];
      return options.map((e) => ({
        id: e.id!,
        value: `${e.name} (${e.url})`,
      }));
    } catch (e) {
      console.log(e);
    }
    return [];
  }, []);
  const validator = (_: any, value: any[]) => {
    if (user?.is_super_admin) {
      return Promise.resolve();
    }
    const filteredValues = value.filter(
      (e) => !e?.__action || e?.__action !== "delete"
    );

    if (filteredValues.length === 0) {
      return Promise.reject("You must select at least one url");
    }
    const hasValidUrl = filteredValues.some((e) => e?.root_url);
    if (!hasValidUrl) {
      return Promise.reject("Invalid url");
    }
    return Promise.resolve();
  };
  const upload = useCallback(async (file: File) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await HttpClient.post(`gear/upload`, form);
      if (data?.data?.filename) {
        return get_file_url(data.data.filename);
      }
    } catch (e) {
      console.log(e);
    }
    throw new Error("Upload failed");
  }, []);

  const deleteImages = useCallback(async (images: string[]) => {
    if (images.length === 0) {
      return;
    }
    try {
      const files = images
        .map((e) => e.split("/").pop()!)
        .map((e) => encodeURIComponent(e))
        .join(",");
      await HttpClient.delete(`gear/upload?files=${files}`);
    } catch (e) {
      console.log(e);
    }
  }, []);

  return (
    <Form
      name="basic"
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
      onFinish={save}
      autoComplete="off"
      form={form}
      initialValues={{
        is_public: true,
        ...initialValue,
        url: cleanUrl(initialValue?.url),
      }}
    >
      {serverMsg && (
        <Alert
          type={serverMsgType}
          message={serverMsg}
          style={{ marginBottom: 10 }}
        />
      )}
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: "Gear name required" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Urls"
        name="urls"
        rules={[{ validator, validateTrigger: "onSubmit" }]}
      >
        <GearUrl />
      </Form.Item>
      <Form.Item
        label="Description"
        name="description"
        rules={[
          {
            required: true,
            message: "Gear description required",
          },
        ]}
      >
        <RichEditor
          uploadImage={upload}
          deleteImages={deleteImages}
          searchGears={searchGears}
        />
      </Form.Item>
      <Form.Item label="Tags" name="tags">
        <TagList />
      </Form.Item>
      <Form.Item label="Status" name="is_public">
        <Select disabled={mode === Mode.edit && !self}>
          <Select.Option value={true}>Public</Select.Option>
          <Select.Option value={false}>Private</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        wrapperCol={{ sm: { offset: 4, span: 16 }, xs: { offset: 0 } }}
      >
        <Space>
          <ActionBtn
            type="primary"
            size="small"
            htmlType="submit"
            busy={busy}
            icon={<SaveOutlined />}
          >
            Save
          </ActionBtn>
          {mode === Mode.edit && !disableDelete && (
            <DeleteBtn
              path={`gear/${initialValue?.id}`}
              onDone={() => {
                onDelete && onDelete(initialValue as any);
              }}
              httpClient={httpClient}
            />
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default GearForm;
